import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import './LegalPages.css';

const CONTACT_EMAIL = 'admin@szybkieprawko.online';
const LAST_UPDATED = '6 czerwca 2026 r.';

function LegalPageShell({
  eyebrow,
  title,
  lead,
  children,
}: {
  eyebrow: string;
  title: string;
  lead: string;
  children: ReactNode;
}) {
  return (
    <main className="legal-page">
      <section className="legal-page__hero">
        <p className="legal-page__eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{lead}</p>
      </section>
      <article className="legal-page__content">
        {children}
      </article>
    </main>
  );
}

function ContactLink() {
  return <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;
}

export function AboutPage() {
  return (
    <LegalPageShell
      eyebrow="O projekcie"
      title="O nas"
      lead="Szybkie Prawko to serwis edukacyjny pomagający przygotować się do egzaminu teoretycznego na prawo jazdy."
    >
      <h2>Czym jest Szybkie Prawko?</h2>
      <p>
        Szybkie Prawko udostępnia narzędzia do nauki pytań egzaminacyjnych, tryb egzaminu próbnego,
        historię wyników oraz materiały ułatwiające systematyczne przygotowanie do egzaminu.
      </p>

      <h2>Dla kogo jest serwis?</h2>
      <p>
        Serwis jest przeznaczony dla osób uczących się do egzaminu teoretycznego na prawo jazdy oraz
        dla użytkowników, którzy chcą utrwalać wiedzę z zakresu przepisów ruchu drogowego.
      </p>

      <h2>Model działania</h2>
      <p>
        Część funkcji serwisu jest dostępna bezpłatnie. Konto Premium usuwa reklamy i odblokowuje
        dodatkowe funkcje, w tym wygodniejszą naukę oraz dostęp do historii egzaminów.
      </p>

      <h2>Kontakt</h2>
      <p>
        W sprawach związanych z działaniem serwisu skontaktuj się z nami pod adresem <ContactLink />.
      </p>
    </LegalPageShell>
  );
}

export function ContactPage() {
  return (
    <LegalPageShell
      eyebrow="Kontakt"
      title="Skontaktuj się z nami"
      lead="Masz pytanie dotyczące konta, płatności, działania aplikacji albo treści w serwisie? Napisz do nas."
    >
      <h2>Dane kontaktowe</h2>
      <p>
        Adres email do kontaktu: <ContactLink />.
      </p>

      <h2>W jakich sprawach możesz napisać?</h2>
      <ul>
        <li>problemy techniczne z aplikacją, logowaniem lub kontem,</li>
        <li>pytania dotyczące konta Premium i płatności,</li>
        <li>zgłoszenia błędów w działaniu serwisu,</li>
        <li>pytania dotyczące prywatności, danych osobowych lub usunięcia konta,</li>
        <li>zgłoszenia dotyczące treści lub działania reklam.</li>
      </ul>

      <h2>Czas odpowiedzi</h2>
      <p>
        Staramy się odpowiadać na wiadomości możliwie szybko. W sprawach wymagających dodatkowej
        weryfikacji odpowiedź może zająć więcej czasu.
      </p>
    </LegalPageShell>
  );
}

export function PrivacyPolicyPage() {
  return (
    <LegalPageShell
      eyebrow="Prywatność"
      title="Polityka prywatności"
      lead="Poniżej opisujemy, jakie dane mogą być przetwarzane podczas korzystania z serwisu Szybkie Prawko."
    >
      <p className="legal-page__meta">Ostatnia aktualizacja: {LAST_UPDATED}</p>

      <h2>1. Administrator i kontakt</h2>
      <p>
        Administratorem danych przetwarzanych w ramach serwisu Szybkie Prawko jest operator serwisu.
        Kontakt w sprawach prywatności i danych osobowych jest możliwy pod adresem <ContactLink />.
      </p>

      <h2>2. Jakie dane przetwarzamy?</h2>
      <p>W zależności od sposobu korzystania z serwisu możemy przetwarzać:</p>
      <ul>
        <li>dane konta, takie jak nazwa użytkownika, adres email i status weryfikacji email,</li>
        <li>informacje o logowaniu, w tym dane techniczne niezbędne do utrzymania sesji,</li>
        <li>preferencje użytkownika, np. język i wybraną kategorię egzaminu,</li>
        <li>wyniki nauki, odpowiedzi, historię testów i postępy,</li>
        <li>informacje o statusie Premium oraz płatnościach obsługiwanych przez zewnętrznego operatora płatności,</li>
        <li>dane techniczne, takie jak adres IP, informacje o przeglądarce, urządzeniu i logach serwera.</li>
      </ul>

      <h2>3. Cele przetwarzania danych</h2>
      <p>Dane przetwarzamy w celu:</p>
      <ul>
        <li>prowadzenia kont użytkowników i umożliwienia logowania,</li>
        <li>udostępniania funkcji nauki, egzaminów próbnych i historii wyników,</li>
        <li>obsługi kont Premium i płatności,</li>
        <li>zapewnienia bezpieczeństwa, przeciwdziałania nadużyciom i diagnozowania błędów,</li>
        <li>obsługi korespondencji wysłanej na adres kontaktowy,</li>
        <li>wyświetlania reklam użytkownikom bez aktywnego konta Premium.</li>
      </ul>

      <h2>4. Reklamy Google AdSense</h2>
      <p>
        Serwis może korzystać z Google AdSense. Oznacza to, że Google oraz jego partnerzy mogą używać
        plików cookie lub podobnych technologii do wyświetlania reklam, ograniczania liczby ich wyświetleń,
        pomiaru skuteczności reklam i zapobiegania nadużyciom.
      </p>
      <p>
        Reklamy są przeznaczone dla użytkowników bez aktywnego konta Premium. Po zalogowaniu na konto
        Premium skrypt reklamowy nie jest ładowany przez aplikację.
      </p>
      <p>
        Informacje o tym, jak Google wykorzystuje dane z witryn i aplikacji korzystających z jego usług,
        są dostępne na stronie: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noreferrer">policies.google.com/technologies/partner-sites</a>.
      </p>

      <h2>5. Pliki cookie i podobne technologie</h2>
      <p>
        Serwis może używać plików cookie lub pamięci lokalnej przeglądarki do utrzymania sesji,
        przechowywania tokenów logowania, zapamiętania ustawień oraz obsługi funkcji aplikacji.
        Zewnętrzni dostawcy, w tym Google, mogą używać własnych technologii w związku z reklamami.
      </p>

      <h2>6. Płatności</h2>
      <p>
        Płatności za Premium są obsługiwane przez zewnętrznego operatora płatności Stripe. Serwis nie
        przechowuje pełnych danych kart płatniczych. Dane niezbędne do realizacji płatności, obsługi
        transakcji, przeciwdziałania nadużyciom i potwierdzenia zakupu mogą być przetwarzane przez Stripe
        zgodnie z zasadami prywatności tego operatora.
      </p>
      <p>
        Informacje o prywatności Stripe są dostępne na stronie: <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a>.
      </p>

      <h2>7. Odbiorcy danych</h2>
      <p>
        Dane mogą być przekazywane dostawcom usług technicznych, hostingu, poczty email, płatności,
        bezpieczeństwa oraz reklam, wyłącznie w zakresie potrzebnym do działania serwisu.
      </p>

      <h2>8. Prawa użytkownika</h2>
      <p>
        Użytkownik może skontaktować się z nami w celu uzyskania informacji o danych, ich sprostowania,
        ograniczenia przetwarzania, usunięcia konta lub zgłoszenia sprzeciwu w zakresie przewidzianym
        przez obowiązujące przepisy.
      </p>

      <h2>9. Usunięcie konta i okres przechowywania danych</h2>
      <p>
        Użytkownik może usunąć konto samodzielnie z panelu użytkownika dostępnego po zalogowaniu.
        Usunięcie konta oznacza usunięcie dostępu do konta oraz danych powiązanych z korzystaniem z
        serwisu, z zastrzeżeniem danych, które muszą być przechowywane dłużej ze względu na rozliczenia,
        bezpieczeństwo, dochodzenie roszczeń lub obowiązki prawne.
      </p>
      <p>
        W przypadku konta bez aktywnego Premium dane użytkownika są usuwane od razu po skutecznym
        złożeniu żądania usunięcia konta w panelu użytkownika.
      </p>
      <p>
        W przypadku konta z aktywnym Premium dane związane z kontem, płatnością i realizacją usługi
        Premium mogą być przechowywane przez okres do 5 lat od usunięcia konta, w szczególności w celu
        obsługi rozliczeń, reklamacji, zwrotów, bezpieczeństwa i ewentualnych roszczeń.
      </p>

      <h2>10. Zmiany polityki prywatności</h2>
      <p>
        Polityka prywatności może być aktualizowana, szczególnie w przypadku zmian funkcji serwisu,
        dostawców usług lub wymogów prawnych.
      </p>
    </LegalPageShell>
  );
}

export function TermsPage() {
  return (
    <LegalPageShell
      eyebrow="Zasady korzystania"
      title="Regulamin użytkownika"
      lead="Regulamin określa podstawowe zasady korzystania z serwisu Szybkie Prawko."
    >
      <p className="legal-page__meta">Ostatnia aktualizacja: {LAST_UPDATED}</p>

      <h2>1. Postanowienia ogólne</h2>
      <p>
        Szybkie Prawko jest serwisem edukacyjnym udostępniającym funkcje pomocne w przygotowaniu do
        egzaminu teoretycznego na prawo jazdy. Kontakt z operatorem serwisu: <ContactLink />.
      </p>
      <p>
        Korzystanie z serwisu przez użytkownika niezalogowanego oraz korzystanie z konta użytkownika
        zalogowanego w podstawowym zakresie jest nieodpłatne. Użytkownik nie ponosi kosztów korzystania
        ze środka porozumiewania się na odległość wyższych niż zwykle stosowane przez jego dostawcę
        Internetu, operatora telekomunikacyjnego lub dostawcę urządzenia.
      </p>
      <p>
        Konto Premium jest udostępniane odpłatnie. Użytkownik ponosi koszt wybranego pakietu Premium
        na czas określony w ofercie widocznej w serwisie przed zakupem.
      </p>
      <p>
        Użytkownik uzyskuje możliwość korzystania z konta po wprowadzeniu wymaganych danych,
        zaakceptowaniu wymaganych elementów formularza i wybraniu przycisku rejestracji albo po pierwszym
        logowaniu przez zewnętrznego dostawcę tożsamości, np. Google, jeżeli taka metoda logowania jest
        dostępna i użytkownik wyrazi wymagane zgody.
      </p>
      <p>
        Powiadomienia dotyczące konta i korzystania z serwisu mogą być przesyłane na adres email będący
        identyfikatorem konta albo podany przez użytkownika podczas rejestracji lub logowania.
      </p>
      <p>
        Użytkownik jest zobowiązany do korzystania z usług i treści dostępnych w serwisie zgodnie z
        obowiązującym prawem, dobrymi obyczajami, normami społecznymi oraz regulaminem. Korzystanie z
        serwisu z naruszeniem prawa może wiązać się z odpowiedzialnością cywilną lub karną. W zakresie
        wymaganym przez obowiązujące przepisy serwis może przekazać posiadane dane użytkownika
        uprawnionym organom państwa.
      </p>
      <p>
        Serwis może korzystać z informacji publicznych, w tym oficjalnych państwowych pytań egzaminu
        teoretycznego, o ile takie informacje pozostają publicznie dostępne. Jeżeli dostępność lub status
        prawny takich materiałów ulegnie zmianie, serwis może ograniczyć, zmienić albo zakończyć ich
        udostępnianie.
      </p>

      <h2>2. Charakter serwisu</h2>
      <p>
        Treści i funkcje w serwisie mają charakter edukacyjny. Serwis nie gwarantuje uzyskania
        pozytywnego wyniku egzaminu państwowego i nie zastępuje oficjalnych materiałów, przepisów ani
        szkolenia prowadzonego przez uprawnione podmioty.
      </p>

      <h2>3. Konto użytkownika</h2>
      <p>
        Użytkownik może utworzyć konto, aby korzystać z funkcji wymagających logowania, takich jak
        zapisywanie postępów, historia wyników, preferencje i konto Premium. Użytkownik powinien podawać
        prawdziwy adres email i chronić dane logowania przed dostępem osób trzecich.
      </p>

      <h2>4. Zasady korzystania</h2>
      <p>Podczas korzystania z serwisu zabronione jest:</p>
      <ul>
        <li>używanie serwisu w sposób naruszający prawo lub prawa innych osób,</li>
        <li>próbowanie obejścia zabezpieczeń, limitów lub mechanizmów autoryzacji,</li>
        <li>automatyczne pobieranie, kopiowanie lub scrapowanie treści bez zgody operatora,</li>
        <li>zakłócanie działania serwisu lub infrastruktury technicznej,</li>
        <li>wykorzystywanie konta w sposób sprzeczny z przeznaczeniem serwisu.</li>
      </ul>

      <h2>5. Konto Premium</h2>
      <p>
        Konto Premium zapewnia dodatkowe funkcje wskazane w serwisie, w szczególności korzystanie z
        wybranych funkcji bez reklam. Zakres funkcji Premium może być rozwijany lub zmieniany wraz z
        rozwojem aplikacji.
      </p>

      <h2>6. Płatności</h2>
      <p>
        Wszystkie płatności za pakiety Premium są jednorazowe. Użytkownik wykupuje pakiet Premium na
        określony czas widoczny w serwisie przed zakupem. Serwis nie pobiera cyklicznych opłat,
        automatycznych odnowień ani innych opłat poza ceną wybranego pakietu.
      </p>
      <p>
        Po zakończeniu okresu ważności wykupionego pakietu użytkownik traci dostęp do funkcji Premium,
        a konto wraca do zakresu funkcji właściwego dla użytkownika bez aktywnego Premium.
      </p>
      <p>
        Płatności elektroniczne i płatności kartą płatniczą są realizowane za pośrednictwem Stripe.
        Aktualnie dostępne metody płatności są prezentowane w formularzu płatności Stripe podczas
        składania zamówienia. Stripe może przetwarzać dane płatnicze zgodnie z własnymi regulaminami
        i politykami prywatności.
      </p>
      <p>
        Serwis nie przechowuje pełnych danych kart płatniczych użytkowników. Obsługa danych płatniczych,
        autoryzacja transakcji i rozliczenie płatności odbywają się po stronie Stripe.
      </p>

      <h2>7. Aktywacja konta Premium po dokonaniu płatności</h2>
      <p>
        Dostęp Premium jest aktywowany po prawidłowym potwierdzeniu płatności przez Stripe lub po użyciu
        ważnego kodu Premium. Aktywacja może nastąpić automatycznie po otrzymaniu potwierdzenia płatności
        przez serwis.
      </p>
      <p>
        Aktywacja Premium oznacza rozpoczęcie świadczenia usługi cyfrowej w płatnym zakresie, w tym
        udostępnienie funkcji Premium oraz wyłączenie reklam dla danego konta po poprawnym rozpoznaniu
        statusu Premium.
      </p>

      <h2>8. Warunki świadczenia usługi Premium</h2>
      <p>Usługa Premium jest świadczona użytkownikom, którzy:</p>
      <ul>
        <li>posiadają konto użytkownika w serwisie,</li>
        <li>zaakceptowali regulamin,</li>
        <li>zakupili pakiet Premium lub otrzymali dostęp Premium na innych zasadach przewidzianych w serwisie,</li>
        <li>dysponują urządzeniem, przeglądarką internetową i dostępem do Internetu umożliwiającymi korzystanie z serwisu.</li>
      </ul>
      <p>
        Użytkownik może korzystać z konta Premium wyłącznie na własne potrzeby. Udostępnianie konta
        osobom trzecim, odsprzedaż dostępu, obchodzenie zabezpieczeń lub automatyczne wykorzystywanie
        konta jest zabronione.
      </p>
      <p>
        Dostęp do usługi Premium jest co do zasady nieograniczony terytorialnie, o ile dostęp do serwisu
        nie jest ograniczony przez przepisy prawa, operatorów infrastruktury, dostawców usług lub warunki
        techniczne niezależne od serwisu.
      </p>

      <h2>9. Prawo odstąpienia od umowy</h2>
      <p>
        Prawo odstąpienia od umowy przysługuje użytkownikom będącym konsumentami na zasadach określonych
        w obowiązujących przepisach prawa.
      </p>
      <p>
        Użytkownik przyjmuje do wiadomości, że aktywacja pakietu Premium może oznaczać rozpoczęcie
        świadczenia usługi cyfrowej przed upływem ustawowego terminu odstąpienia od umowy. Jeżeli
        zgodnie z obowiązującymi przepisami użytkownik wyrazi zgodę na rozpoczęcie świadczenia usługi
        cyfrowej przed upływem terminu odstąpienia i zostanie poinformowany o skutkach takiej zgody,
        prawo odstąpienia może nie przysługiwać po aktywacji dostępu Premium.
      </p>
      <p>
        W sprawach dotyczących odstąpienia od umowy lub zwrotu płatności użytkownik może skontaktować
        się z serwisem pod adresem <ContactLink />.
      </p>

      <h2>10. Reklamacje</h2>
      <p>
        Reklamacje dotyczące działania serwisu, konta Premium, aktywacji pakietu, płatności lub innych
        usług można składać drogą elektroniczną na adres <ContactLink />.
      </p>
      <p>
        Reklamacja powinna zawierać adres email konta, którego dotyczy zgłoszenie, opis problemu oraz,
        jeżeli dotyczy płatności, informacje pozwalające zidentyfikować transakcję bez przekazywania
        pełnych danych karty płatniczej.
      </p>
      <p>
        Reklamacje będą rozpatrywane w terminie do 14 dni od dnia ich otrzymania. Odpowiedź zostanie
        przesłana na adres email wskazany przez użytkownika lub przypisany do konta.
      </p>

      <h2>11. Reklamy</h2>
      <p>
        Użytkownicy bez aktywnego konta Premium mogą widzieć reklamy, w tym reklamy dostarczane przez
        Google AdSense. Konto Premium wyłącza ładowanie reklam w aplikacji po poprawnym rozpoznaniu
        statusu Premium.
      </p>

      <h2>12. Dostępność i zmiany w serwisie</h2>
      <p>
        Dokładamy starań, aby serwis działał stabilnie, ale nie gwarantujemy nieprzerwanej dostępności.
        Serwis może być czasowo niedostępny z powodu prac technicznych, awarii lub czynników niezależnych
        od operatora.
      </p>

      <h2>13. Odpowiedzialność</h2>
      <p>
        Użytkownik korzysta z serwisu na własną odpowiedzialność. Operator nie odpowiada za decyzje
        podjęte wyłącznie na podstawie treści serwisu ani za wynik egzaminu państwowego.
      </p>

      <h2>14. Usunięcie konta</h2>
      <p>
        Użytkownik może usunąć konto samodzielnie z panelu użytkownika dostępnego po zalogowaniu.
        Usunięcie konta powoduje usunięcie dostępu do konta oraz danych powiązanych z korzystaniem z
        serwisu zgodnie z zasadami opisanymi w Polityce prywatności.
      </p>
      <p>
        W przypadku konta bez aktywnego Premium dane użytkownika są usuwane od razu po skutecznym
        usunięciu konta z panelu użytkownika.
      </p>
      <p>
        W przypadku konta z aktywnym Premium dane związane z kontem, płatnością i realizacją usługi
        Premium mogą być przechowywane przez okres do 5 lat, w szczególności ze względu na rozliczenia,
        reklamacje, zwroty, bezpieczeństwo i ewentualne roszczenia.
      </p>

      <h2>15. Prywatność</h2>
      <p>
        Zasady przetwarzania danych opisuje <Link to="/polityka-prywatnosci">Polityka prywatności</Link>.
      </p>

      <h2>16. Kontakt i zgłoszenia</h2>
      <p>
        Pytania, zgłoszenia błędów, reklamacje techniczne i sprawy dotyczące konta można kierować na
        adres <ContactLink />.
      </p>
    </LegalPageShell>
  );
}
